import { t } from 'i18next';

import FileInput from '#components/general/FileInput';
import Input from '#components/general/Input';
import { ACCEPT_ONLY_PDFS } from '#constants/files';
import Button from '#components/general/Button';
import AdelphiFieldsRow from '#components/business/AdelphiFieldsRow';
import TrashImage from '#assets/delete.svg';
import { getSubStepValues } from '#models/general/steps';

import { MATURATION_FIELDS, NEW_PRODUCT_SUBSTEPS_IDS } from './constants';
import styles from './styles.module.scss';
import { TEMPLATES_OPTIONS } from './selectTemplate';

const newMaturation = (removeMaturation, id) => (getMaturationValues, getMaturationNotes, values) =>
  [
    {
      key: `hr-${id}`,
      Component: () => <hr />
    },
    {
      Component: AdelphiFieldsRow,
      secondGrow: true,
      fields: [
        {
          key: `removeMaturation-${id}`,
          Component: Button,
          children: <img src={TrashImage} className={styles.deleteAlt} />,
          onClick: () => removeMaturation(getMaturationNotes)
        },
        {
          ...getMaturationValues(`${MATURATION_FIELDS.TITLE}-${id}`),
          label: t(`${NEW_PRODUCT_SUBSTEPS_IDS.MATURATION}:${MATURATION_FIELDS.TITLE}`),
          Component: Input
        }
      ]
    },
    {
      ...getMaturationValues(`${MATURATION_FIELDS.CASK_TYPE}-${id}`),
      label: t(`${NEW_PRODUCT_SUBSTEPS_IDS.MATURATION}:${MATURATION_FIELDS.CASK_TYPE}`),
      Component: Input,
      noMargin: true
    },
    {
      ...getMaturationValues(`${MATURATION_FIELDS.WOOD_FILL}-${id}`),
      label: t(`${NEW_PRODUCT_SUBSTEPS_IDS.MATURATION}:${MATURATION_FIELDS.WOOD_FILL}`),
      Component: Input,
      noMargin: true
    },
    {
      ...getMaturationValues(`${MATURATION_FIELDS.SPIRIT_TYPE}-${id}`),
      label: t(`${NEW_PRODUCT_SUBSTEPS_IDS.MATURATION}:${MATURATION_FIELDS.SPIRIT_TYPE}`),
      Component: Input,
      noMargin: true
    },
    {
      ...getMaturationValues(`${MATURATION_FIELDS.VINTAGE}-${id}`),
      label: t(`${NEW_PRODUCT_SUBSTEPS_IDS.MATURATION}:${MATURATION_FIELDS.VINTAGE}`),
      Component: Input,
      noMargin: true
    },
    {
      ...getMaturationValues(`${MATURATION_FIELDS.CASK_NO}-${id}`),
      label: t(`${NEW_PRODUCT_SUBSTEPS_IDS.MATURATION}:${MATURATION_FIELDS.CASK_NO}`),
      Component: Input,
      noMargin: true
    },
    {
      ...getMaturationValues(`${MATURATION_FIELDS.MATURATION_FILE}-${id}`),
      label: t(`${NEW_PRODUCT_SUBSTEPS_IDS.MATURATION}:${MATURATION_FIELDS.MATURATION_FILE}`),
      Component: FileInput,
      accept: ACCEPT_ONLY_PDFS,
      noMargin: true,
      srcUrl:
        typeof values?.[`${MATURATION_FIELDS.MATURATION_FILE}-${id}`] === 'string'
          ? values?.[`${MATURATION_FIELDS.MATURATION_FILE}-${id}`]
          : undefined,
      fileType: 'pdf'
    }
  ];

const mapInitialMaturation = id => ({
  component: removeMaturation => newMaturation(removeMaturation(id), id),
  id
});

export const mapInitialMaturations = ids => ids?.map(id => mapInitialMaturation(id));

export const getMaturationFields = ({ setValue, values, errors, setSubStepState, subStepState }) => {
  const getMaturationValues = getSubStepValues(NEW_PRODUCT_SUBSTEPS_IDS.MATURATION, values, errors);

  const isPackTemplate = values?.templateType === TEMPLATES_OPTIONS.PACK;

  const getCurrentMaturationNotes = () => (subStepState?.maturationNotes ? subStepState.maturationNotes : []);

  const addNote = () => {
    const lastId =
      getCurrentMaturationNotes()
        .map(({ id }) => id)
        .findLast(() => true) || 0;
    const newId = lastId + 1;
    setSubStepState({
      ...subStepState,
      maturationNotes: [
        ...getCurrentMaturationNotes(),
        {
          component: removeSubstep => newMaturation(removeSubstep(newId), newId),
          id: newId
        }
      ]
    });
  };

  const removeMaturation = id => () => {
    setSubStepState({
      ...subStepState,
      maturationNotes: getCurrentMaturationNotes().filter(({ id: thisId }) => thisId !== id)
    });
    setValue(`${MATURATION_FIELDS.TITLE}-${id}`, undefined);
    setValue(`${MATURATION_FIELDS.CASK_TYPE}-${id}`, undefined);
    setValue(`${MATURATION_FIELDS.WOOD_FILL}-${id}`, undefined);
    setValue(`${MATURATION_FIELDS.SPIRIT_TYPE}-${id}`, undefined);
    setValue(`${MATURATION_FIELDS.VINTAGE}-${id}`, undefined);
    setValue(`${MATURATION_FIELDS.CASK_NO}-${id}`, undefined);
    setValue(`${MATURATION_FIELDS.MATURATION_FILE}-${id}`, undefined);
  };

  return [
    ...(getCurrentMaturationNotes()?.length > 0
      ? [
          {
            ...getMaturationValues(`${MATURATION_FIELDS.TITLE}-0`),
            label: t(`${NEW_PRODUCT_SUBSTEPS_IDS.MATURATION}:${MATURATION_FIELDS.TITLE}`),
            Component: Input,
            rows: 3,
            noMargin: true
          }
        ]
      : []),
    {
      ...getMaturationValues(`${MATURATION_FIELDS.CASK_TYPE}-0`),
      label: t(`${NEW_PRODUCT_SUBSTEPS_IDS.MATURATION}:${MATURATION_FIELDS.CASK_TYPE}`),
      Component: Input,
      noMargin: true
    },
    {
      ...getMaturationValues(`${MATURATION_FIELDS.WOOD_FILL}-0`),
      label: t(`${NEW_PRODUCT_SUBSTEPS_IDS.MATURATION}:${MATURATION_FIELDS.WOOD_FILL}`),
      Component: Input,
      noMargin: true
    },
    {
      ...getMaturationValues(`${MATURATION_FIELDS.SPIRIT_TYPE}-0`),
      label: t(`${NEW_PRODUCT_SUBSTEPS_IDS.MATURATION}:${MATURATION_FIELDS.SPIRIT_TYPE}`),
      Component: Input,
      noMargin: true
    },
    {
      ...getMaturationValues(`${MATURATION_FIELDS.VINTAGE}-0`),
      label: t(`${NEW_PRODUCT_SUBSTEPS_IDS.MATURATION}:${MATURATION_FIELDS.VINTAGE}`),
      Component: Input,
      noMargin: true
    },
    {
      ...getMaturationValues(`${MATURATION_FIELDS.CASK_NO}-0`),
      label: t(`${NEW_PRODUCT_SUBSTEPS_IDS.MATURATION}:${MATURATION_FIELDS.CASK_NO}`),
      Component: Input,
      noMargin: true
    },
    {
      ...getMaturationValues(`${MATURATION_FIELDS.MATURATION_FILE}-0`),
      label: t(`${NEW_PRODUCT_SUBSTEPS_IDS.MATURATION}:${MATURATION_FIELDS.MATURATION_FILE}`),
      Component: FileInput,
      accept: ACCEPT_ONLY_PDFS,
      noMargin: true,
      srcUrl:
        typeof values?.[`${MATURATION_FIELDS.MATURATION_FILE}-0`] === 'string'
          ? values?.[`${MATURATION_FIELDS.MATURATION_FILE}-0`]
          : undefined,
      fileType: 'pdf'
    },
    ...getCurrentMaturationNotes()?.flatMap(({ component }) =>
      component(removeMaturation)(getMaturationValues, getCurrentMaturationNotes, values)
    ),
    ...(isPackTemplate
      ? [
          {
            key: 'addMaturation',
            Component: Button,
            children: t(`${NEW_PRODUCT_SUBSTEPS_IDS.MATURATION}:addMaturationButton`),
            onClick: addNote,
            secondary: true
          }
        ]
      : [])
  ];
};
